import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  GetPropertiesQueryVariables,
  LocationType,
  PropertyModel,
  PropertyStatus,
} from 'src/gql/graphql';
import { useGetPropertiesQuery } from 'src/services/properties';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTable } from 'src/components/table';
import { compact } from 'lodash';
import { Button, Grid, InputAdornment } from '@mui/material';
import { RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { PropertyTableListSmall } from './property-table-small';
import { RHFMultiSelectPropertyStatusField } from './fields/rhf-property-status-field';
import { RHFSelectLocationField } from './fields/rhf-select-location-field';
import { RHFMultiSelectFeatureField } from './fields/rhf-select-feature-field';

// ----------------------------------------------------------------------

type Props = {
  title?: string;
  action?: React.ReactNode;
  open: boolean;

  defaultSubType?: string[];

  onClose: VoidFunction;
  selected?: (selectedId: string) => boolean;
  onSelect: (item: Item | null) => void;
};

type Item = DeepPartial<PropertyModel>;

export default function PropertyListDialog({
  title = 'Select property',
  action,
  open,
  defaultSubType,
  onClose,
  selected,
  onSelect,
}: Props) {
  const form = useForm<GetPropertiesQueryVariables>({
    defaultValues: {
      status: [PropertyStatus.Online, PropertyStatus.Unknown],
      subType: defaultSubType ?? [],
      search: '',
      provinceId: '',
      municipalityId: '',
      areaId: '',
    },
  });

  const { watch } = form;

  const variables = watch();

  const table = useTable({
    defaultCurrentPage: 0,
    defaultRowsPerPage: 10,
  });

  // Get all properties
  const query = useGetPropertiesQuery({
    ...variables,
    limit: table.rowsPerPage,
    offset: table.page * table.rowsPerPage,
  });

  const items = query.data?.items ?? [];
  const isNotFound = query.isFetched && !items.length;
  const statuses = compact(
    Array.isArray(variables.status) ? variables.status : [variables.status]
  );
  const total = (query.data?.statuses ?? [])
    .filter((x) => statuses.includes(x.key as PropertyStatus))
    ?.reduce((acc, x) => acc + x.count, 0);

  return (
    <FormProvider {...form}>
      <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 3, pr: 1.5 }}
        >
          <Typography variant="h6">{title}</Typography>

          {action && action}
        </Stack>

        <Stack
          gap={1.5}
          sx={{ p: 2, pt: 0, flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Grid container columnSpacing={2} rowSpacing={3}>
            <Grid item sm={6} md={4}>
              <RHFSelectLocationField
                type={LocationType.Province}
                name="provinceId"
                label="Province"
                fullWidth
              />
            </Grid>

            <Grid item sm={6} md={4}>
              <RHFSelectLocationField
                type={LocationType.Municipality}
                parentName="provinceId"
                name="municipalityId"
                label="Municipality"
                fullWidth
              />
            </Grid>

            <Grid item sm={6} md={4}>
              <RHFSelectLocationField
                requiredParent
                type={LocationType.Area}
                parentName="municipalityId"
                name="areaId"
                label="Area"
                fullWidth
              />
            </Grid>

            <Grid item sm={6} md={3}>
              <RHFMultiSelectFeatureField
                name="subType"
                label="Sub Type"
                startsWith="SubType_"
                checkbox
                fullWidth
              />
            </Grid>

            <Grid item sm={6} md={3}>
              <RHFMultiSelectPropertyStatusField fullWidth name="status" />
            </Grid>

            <Grid item sm={6}>
              <RHFTextField
                name="search"
                placeholder="Search..."
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="mdi:search" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Stack>

        <PropertyTableListSmall
          table={table}
          items={query.data?.items ?? []}
          isLoading={query.isLoading}
          isNotFound={isNotFound}
          total={total}
          selected={selected}
          onSelect={onSelect}
        />
      </Dialog>
    </FormProvider>
  );
}
